import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';
import type { Client, Project, Quotation } from '../../types/Quotation';
import { PROJECT_STATUSES } from '../../types/Quotation';
import {
  computeTotals, lineGeneralTotal, componentLineTotal, componentSellingUnit, serviceLineAmount, manpowerCost,
  formatDiscountPct,
} from './calc';
import { quotationExportRefNo } from './codes';
import { subheaderBefore } from './subheaders';

const PHP_FMT = '"₱" #,##0.00;[Red]"₱" -#,##0.00';
const QTY_FMT = '#,##0.00';

function quotationDate(value: string | undefined): Date {
  const dateOnly = (value || format(new Date(), 'yyyy-MM-dd')).slice(0, 10);
  return new Date(`${dateOnly}T00:00:00`);
}

export async function exportQuotationXlsx(
  quotation: Quotation, project: Project, recipient: Client | null, customer: Client | null,
) {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'IOCT Calcsheet';
  wb.created = new Date();

  const totals = computeTotals(quotation);
  // IOCT-only pricing buffer folded into markup — never printed as its own
  // line/label. See Quotation.ewtPct.
  const ewtPct = quotation.ewtPct || 0;
  const refNo = quotationExportRefNo(project.code, recipient?.code, quotation.revision);
  const generalReqtsExportQty = Math.max(1, quotation.generalReqtsExportQty || 1);
  const generalReqtsExportUnitPrice = totals.generalReqtsSubtotal / generalReqtsExportQty;
  const engineeringServicesQty = Math.max(1, quotation.engineeringServicesQty || 1);
  const engineeringServicesUnitPrice = totals.servicesSubtotal / engineeringServicesQty;

  // Header sheet (quotation summary)
  const ws = wb.addWorksheet('Quotation', {
    properties: { defaultRowHeight: 16 },
    pageSetup: { paperSize: 9, orientation: 'portrait', fitToPage: true, fitToWidth: 1 },
  });

  ws.columns = [
    { width: 14 }, { width: 50 }, { width: 10 }, { width: 8 }, { width: 18 }, { width: 18 },
  ];
  // Column C only ever holds quantities — always show 2 decimals (1 -> 1.00),
  // matching the PDF. Text headers in this column are unaffected by numFmt.
  ws.getColumn(3).numFmt = QTY_FMT;

  const navy = 'FF0F2A44';
  const grayBg = 'FFF2F4F7';

  ws.mergeCells('A1:F1');
  ws.getCell('A1').value = quotation.kind === 'IOCT' ? 'IO Control Technologie OPC' : 'Advance Controle Technologie Inc.';
  ws.getCell('A1').font = { name: 'Inter', size: 14, bold: true, color: { argb: navy } };

  ws.mergeCells('A2:F2');
  ws.getCell('A2').value = quotation.kind === 'IOCT' ? 'B63 Biñan, Laguna · TIN: 697-029-976-00000' : 'Blk. 13 Lot 5, Mindanao Ave., Gavino Maderan, General Mariano Alvarez, Cavite 4117, Philippines';
  ws.getCell('A2').font = { name: 'Inter', size: 8, color: { argb: 'FF666666' } };

  ws.getCell('E1').value = 'QUOTATION';
  ws.getCell('E1').font = { name: 'Inter', size: 12, bold: true, color: { argb: navy } };
  ws.getCell('E1').alignment = { horizontal: 'right' };
  ws.getCell('F2').value = `Ref: ${refNo}`;
  ws.getCell('F2').alignment = { horizontal: 'right' };
  ws.getCell('F3').value = `Date: ${format(quotationDate(quotation.dateSent), 'dd MMM yyyy')}`;
  ws.getCell('F3').alignment = { horizontal: 'right' };

  ws.mergeCells('A4:F4');
  ws.getCell('A4').value = project.name;
  ws.getCell('A4').font = { name: 'Inter', size: 14, bold: true };

  ws.getCell('A6').value = 'To:';
  ws.getCell('A6').font = { bold: true };
  ws.getCell('B6').value = recipient?.name ?? '';
  ws.getCell('A7').value = 'Customer:';
  ws.getCell('A7').font = { bold: true };
  ws.getCell('B7').value = customer?.name ?? '';
  ws.getCell('A8').value = 'Location:';
  ws.getCell('A8').font = { bold: true };
  ws.getCell('B8').value = project.location ?? '';

  let r = 10;

  const sectionHeader = (label: string) => {
    ws.mergeCells(`A${r}:F${r}`);
    const c = ws.getCell(`A${r}`);
    c.value = label;
    c.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: navy } };
    c.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
    r++;
  };

  const tableHeader = (cols: string[]) => {
    cols.forEach((label, i) => {
      const cell = ws.getCell(r, i + 1);
      cell.value = label;
      cell.font = { bold: true, size: 9 };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: grayBg } };
      cell.border = { top: { style: 'thin' }, bottom: { style: 'thin' } };
      if (['Qty', 'Unit Price', 'Total'].includes(label)) cell.alignment = { horizontal: 'right' };
    });
    r++;
  };

  const inlineSubheader = (label: string) => {
    ws.mergeCells(`A${r}:F${r}`);
    const cell = ws.getCell(`A${r}`);
    cell.value = label;
    cell.font = { bold: true, color: { argb: navy } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEAF0F8' } };
    cell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
    r++;
  };

  // A. General Requirements
  if (quotation.generalReqts.length) {
    sectionHeader('A. GENERAL REQUIREMENTS');
    tableHeader(['Code', 'Description', 'Qty', 'UOM', 'Unit Price', 'Total']);
    if (quotation.exportGeneralReqtsAsLot) {
      const displayIdx = Math.max(0, Math.floor((quotation.generalReqts.length - 1) / 2));
      quotation.generalReqts.forEach((l, i) => {
        ws.getRow(r).values = [
          l.code,
          l.description,
          i === displayIdx ? generalReqtsExportQty : '',
          i === displayIdx ? 'lot' : '',
          i === displayIdx ? generalReqtsExportUnitPrice : '',
          i === displayIdx ? totals.generalReqtsSubtotal : '',
        ];
        ws.getCell(r, 5).numFmt = PHP_FMT;
        ws.getCell(r, 6).numFmt = PHP_FMT;
        r++;
      });
    } else {
      quotation.generalReqts.forEach((l) => {
        ws.getRow(r).values = [l.code, l.description, l.qty, l.uom, l.unitPrice, lineGeneralTotal(l)];
        ws.getCell(r, 5).numFmt = PHP_FMT;
        ws.getCell(r, 6).numFmt = PHP_FMT;
        r++;
      });
    }
    ws.getRow(r).values = ['', '', '', '', 'Subtotal', totals.generalReqtsSubtotal];
    ws.getCell(r, 5).font = { bold: true };
    ws.getCell(r, 6).font = { bold: true };
    ws.getCell(r, 6).numFmt = PHP_FMT;
    r += 2;
  }

  // B. Components (optional items are pulled out into their own section below).
  const contractComponents = quotation.components.filter((l) => !l.optional);
  const optionalComponents = quotation.components.filter((l) => l.optional);
  if (contractComponents.length) {
    sectionHeader('B. SUPPLY OF COMPONENTS');
    tableHeader(['Code', 'Description', 'Qty', 'UOM', 'Unit Price', 'Total']);
    // Group-aware rendering — pricing on middle row of each group
    const compGroups = new Map<string, typeof quotation.components>();
    contractComponents.forEach((l) => {
      if (l.group) {
        const arr = compGroups.get(l.group) || [];
        arr.push(l);
        compGroups.set(l.group, arr);
      }
    });
    contractComponents.forEach((l, index) => {
      if (l.isHeader || l.isChildHeader) {
        ws.mergeCells(`A${r}:F${r}`);
        const c = ws.getCell(`A${r}`);
        c.value = l.description;
        c.font = l.isChildHeader ? { bold: true, italic: true, size: 9, color: { argb: 'FF4F7BC8' } } : { bold: true, color: { argb: 'FF2C5AA0' } };
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: grayBg } };
        c.alignment = { vertical: 'middle', horizontal: 'left', indent: l.isChildHeader ? 3 : 1 };
        r++;
        return;
      }
      const subheader = subheaderBefore(contractComponents, index);
      if (subheader) inlineSubheader(subheader);
      // Item name on the first line; brand + part number on a wrapped
      // second line (mirrors the PDF's two-line description cell).
      const compSub = [l.brand, l.partNo].filter(Boolean).join(', ');
      const desc = compSub ? `${l.description}\n${compSub}` : l.description;
      if (l.group) {
        const members = compGroups.get(l.group)!;
        const midIdx = Math.max(0, Math.floor((members.length - 1) / 2));
        const isMid = members[midIdx].id === l.id;
        // 'itemized' shows each member's own qty + UOM; the group stays priced
        // as one combined amount on the middle row (no per-unit price shown).
        const itemized = quotation.componentGroupDisplay?.[l.group] === 'itemized';
        const groupTotal = isMid
          ? members.reduce((s, m) => s + componentLineTotal(m, quotation.productMarkupPct, ewtPct), 0)
          : 0;
        if (itemized) {
          // Group price shows in both Unit Price and Total on the middle row,
          // same as the LOT presentation — it's one combined lot amount.
          ws.getRow(r).values = [l.code, desc, l.qty, l.uom, isMid ? groupTotal : '', isMid ? groupTotal : ''];
          if (isMid) {
            ws.getCell(r, 5).numFmt = PHP_FMT;
            ws.getCell(r, 6).numFmt = PHP_FMT;
          }
        } else if (isMid) {
          ws.getRow(r).values = [l.code, desc, 1, 'lot', groupTotal, groupTotal];
          ws.getCell(r, 5).numFmt = PHP_FMT;
          ws.getCell(r, 6).numFmt = PHP_FMT;
        } else {
          ws.getRow(r).values = [l.code, desc, '', '', '', ''];
        }
      } else {
        ws.getRow(r).values = [l.code, desc, l.qty, l.uom, componentSellingUnit(l, quotation.productMarkupPct, ewtPct), componentLineTotal(l, quotation.productMarkupPct, ewtPct)];
        ws.getCell(r, 5).numFmt = PHP_FMT;
        ws.getCell(r, 6).numFmt = PHP_FMT;
      }
      if (compSub) ws.getCell(r, 2).alignment = { wrapText: true, vertical: 'top' };
      r++;
    });
    ws.getRow(r).values = ['', '', '', '', 'Subtotal', totals.componentsSubtotal];
    ws.getCell(r, 5).font = { bold: true };
    ws.getCell(r, 6).font = { bold: true };
    ws.getCell(r, 6).numFmt = PHP_FMT;
    r += 2;
  }

  // C. Services
  if (totals.servicesSubtotal > 0) {
    sectionHeader('C. ENGINEERING SERVICES');
    tableHeader(['Code', 'Description', 'Qty', 'UOM', 'Unit Price', 'Total']);
    if (quotation.servicesFromManpower && !quotation.servicesPerLinePricing) {
      ws.getRow(r).values = ['C-0010', 'Engineering Services', engineeringServicesQty, 'lot', engineeringServicesUnitPrice, totals.servicesSubtotal];
      ws.getCell(r, 5).numFmt = PHP_FMT;
      ws.getCell(r, 6).numFmt = PHP_FMT;
      r++;
    } else {
      // Group-aware rendering — pricing on middle row of each group
      const groups = new Map<string, typeof quotation.services>();
      quotation.services.forEach((l) => {
        if (l.group) {
          const arr = groups.get(l.group) || [];
          arr.push(l);
          groups.set(l.group, arr);
        }
      });
      // Per-line-pricing-from-manpower amounts already have EWT baked in at
      // edit time (CalcsheetQuotationEditor's updateServiceRow); manual
      // lump-sum amounts don't, so it's applied here at display time —
      // grouping is only reachable in per-line-pricing mode, manual-mode
      // lines are never grouped (no UI path to set l.group there).
      quotation.services.forEach((l, index) => {
        if (l.isHeader || l.isChildHeader) {
          ws.mergeCells(`A${r}:F${r}`);
          const c = ws.getCell(`A${r}`);
          c.value = l.description;
          c.font = l.isChildHeader ? { bold: true, italic: true, size: 9, color: { argb: 'FF4F7BC8' } } : { bold: true, color: { argb: 'FF2C5AA0' } };
          c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: grayBg } };
          c.alignment = { vertical: 'middle', horizontal: 'left', indent: l.isChildHeader ? 3 : 1 };
          r++;
          return;
        }
        const subheader = subheaderBefore(quotation.services, index);
        if (subheader) inlineSubheader(subheader);
        const itemized = !!quotation.servicesPerLinePricing && !!quotation.servicesItemizedExport;
        const rawQty = l.qty ?? l.days ?? 0;
        const lineQty = rawQty > 0 ? rawQty : 1;
        const lineUom = (l.uom || 'lot').toLowerCase();
        if (l.group) {
          const members = groups.get(l.group)!;
          const midIdx = Math.max(0, Math.floor((members.length - 1) / 2));
          const isMid = members[midIdx].id === l.id;
          if (isMid) {
            const groupTotal = members.reduce((s, m) => s + (m.amount || 0), 0);
            ws.getRow(r).values = [l.code, l.description, 1, 'lot', groupTotal, groupTotal];
            ws.getCell(r, 5).numFmt = PHP_FMT;
            ws.getCell(r, 6).numFmt = PHP_FMT;
          } else if (itemized) {
            ws.getRow(r).values = [l.code, l.description, lineQty, lineUom, '', ''];
          } else {
            ws.getRow(r).values = [l.code, l.description, '', '', '', ''];
          }
          r++;
        } else if (itemized) {
          // Real QTY/UOM, with unit price derived from amount ÷ qty so
          // unit × qty still reconciles to the printed total.
          const amt = l.amount || 0;
          ws.getRow(r).values = [l.code, l.description, lineQty, lineUom, amt / lineQty, amt];
          ws.getCell(r, 5).numFmt = PHP_FMT;
          ws.getCell(r, 6).numFmt = PHP_FMT;
          r++;
        } else {
          const amt = quotation.servicesPerLinePricing ? (l.amount || 0) : serviceLineAmount(l, ewtPct);
          ws.getRow(r).values = [l.code, l.description, 1, 'lot', amt, amt];
          ws.getCell(r, 5).numFmt = PHP_FMT;
          ws.getCell(r, 6).numFmt = PHP_FMT;
          r++;
        }
      });
    }
    ws.getRow(r).values = ['', '', '', '', 'Subtotal', totals.servicesSubtotal];
    ws.getCell(r, 5).font = { bold: true };
    ws.getCell(r, 6).font = { bold: true };
    ws.getCell(r, 6).numFmt = PHP_FMT;
    r += 2;
  }

  // Totals
  const totalsBlock: [string, number | string, boolean?][] = [
    [quotation.nonVat ? 'Subtotal' : 'Subtotal (VAT-EX)', totals.subtotal, true],
  ];
  if (quotation.discountPct > 0) totalsBlock.push([`Discount (${formatDiscountPct(quotation.discountPct)}%)`, -totals.discount]);
  if ((totals.deliveryFee ?? 0) > 0) totalsBlock.push(['Delivery Fee', totals.deliveryFee ?? 0]);
  if ((totals.smallOrderSurcharge ?? 0) > 0) totalsBlock.push(['Small-order surcharge', totals.smallOrderSurcharge ?? 0]);
  if (quotation.nonVat) {
    // Text value in the amount column — printed as-is, no 0% VAT line.
    totalsBlock.push(['VAT', 'N/A (Non-VAT)']);
  } else if (quotation.vatPct > 0) {
    totalsBlock.push([`VAT (${quotation.vatPct}%)`, totals.vat]);
  }
  totalsBlock.push([quotation.nonVat ? 'TOTAL AMOUNT PAYABLE (PHP)' : 'GRAND TOTAL (PHP)', totals.grandTotal, true]);

  totalsBlock.forEach(([label, value, bold]) => {
    ws.getCell(r, 5).value = label;
    ws.getCell(r, 6).value = value;
    ws.getCell(r, 6).numFmt = PHP_FMT;
    if (bold) {
      ws.getCell(r, 5).font = { bold: true };
      ws.getCell(r, 6).font = { bold: true };
    }
    if (label.startsWith('GRAND') || label.startsWith('TOTAL AMOUNT')) {
      ws.getCell(r, 5).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: navy } };
      ws.getCell(r, 6).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: navy } };
      ws.getCell(r, 5).font = { bold: true, color: { argb: 'FFFFFFFF' } };
      ws.getCell(r, 6).font = { bold: true, color: { argb: 'FFFFFFFF' } };
    }
    r++;
  });

  // Optional items — priced for reference, NOT in the grand total above.
  if (optionalComponents.length) {
    r += 1;
    sectionHeader('OPTIONAL ITEMS (NOT INCLUDED IN CONTRACT PRICE)');
    tableHeader(['Code', 'Description', 'Qty', 'UOM', 'Unit Price', 'Total']);
    optionalComponents.forEach((l, index) => {
      const subheader = subheaderBefore(optionalComponents, index);
      if (subheader) inlineSubheader(subheader);
      const compSub = [l.brand, l.partNo].filter(Boolean).join(', ');
      const desc = compSub ? `${l.description}\n${compSub}` : l.description;
      ws.getRow(r).values = [l.code, desc, l.qty, l.uom, componentSellingUnit(l, quotation.productMarkupPct, ewtPct), componentLineTotal(l, quotation.productMarkupPct, ewtPct)];
      if (compSub) ws.getCell(r, 2).alignment = { wrapText: true, vertical: 'top' };
      ws.getCell(r, 5).numFmt = PHP_FMT;
      ws.getCell(r, 6).numFmt = PHP_FMT;
      r++;
    });
    ws.getRow(r).values = ['', '', '', '', 'Optional Total', totals.componentsOptionalSubtotal ?? 0];
    ws.getCell(r, 5).font = { bold: true };
    ws.getCell(r, 6).font = { bold: true };
    ws.getCell(r, 6).numFmt = PHP_FMT;
    r += 2;
  }

  // Manpower sheet (working data)
  if (quotation.manpower.length) {
    const mp = wb.addWorksheet('Manpower');
    mp.columns = [
      { header: 'Role', width: 30 }, { header: 'Group', width: 14 },
      { header: 'Pax', width: 8 }, { header: 'Mandays', width: 10 },
      { header: 'Daily Rate', width: 14 }, { header: 'Allowance', width: 14 },
      { header: 'Cost', width: 16 },
    ];
    mp.getRow(1).font = { bold: true };
    quotation.manpower.forEach((m) => {
      mp.addRow([m.role, m.group, m.headcount, m.mandays, m.dailyRate, m.allowance, manpowerCost(m)]);
    });
    mp.getColumn(5).numFmt = PHP_FMT;
    mp.getColumn(6).numFmt = PHP_FMT;
    mp.getColumn(7).numFmt = PHP_FMT;
  }

  const buf = await wb.xlsx.writeBuffer();
  const filename = `${refNo}.xlsx`;
  saveAs(new Blob([buf]), filename);
}

// ── Project list export (status-review checklist) ──────────────────────────

export interface ProjectListExportRow {
  code: string;
  name: string;
  customer: string;
  partner: string;
  date: string;      // 'dd MMM yyyy' or ''
  status: string;
  ongoing: string;   // 'Yes' | '—'
  notes: string;
  createdBy: string; // creator display name or ''
}

// Derived from the shared status list so the Excel dropdown can never drift
// from the app's ProjectStatus set.
const PROJECT_STATUS_DROPDOWN = `"${PROJECT_STATUSES.join(',')}"`;

export function buildProjectListWorkbook(rows: ProjectListExportRow[]): ExcelJS.Workbook {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'IOCT Calcsheet';
  wb.created = new Date();

  const ws = wb.addWorksheet('Projects', { views: [{ state: 'frozen', ySplit: 1 }] });
  ws.columns = [
    { header: 'Code', width: 20 },
    { header: 'Project Name', width: 45 },
    { header: 'Customer', width: 28 },
    { header: 'Partner', width: 28 },
    { header: 'Date', width: 14 },
    { header: 'Current Status', width: 14 },
    { header: 'Ongoing', width: 9 },
    { header: 'Updated Status', width: 15 },
    { header: 'Remarks', width: 30 },
    { header: 'Notes', width: 40 },
    { header: 'Created By', width: 20 },
  ];
  ws.getRow(1).font = { bold: true };
  ws.autoFilter = 'A1:K1';

  rows.forEach((r, i) => {
    ws.addRow([r.code, r.name, r.customer, r.partner, r.date, r.status, r.ongoing, null, null, r.notes, r.createdBy]);
    // Excel dropdown on the blank Updated Status cell — the whole point of the export.
    ws.getCell(i + 2, 8).dataValidation = {
      type: 'list',
      allowBlank: true,
      formulae: [PROJECT_STATUS_DROPDOWN],
    };
  });

  return wb;
}

export async function exportProjectListXlsx(rows: ProjectListExportRow[]): Promise<void> {
  const wb = buildProjectListWorkbook(rows);
  const buf = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buf]), `calcsheet-projects-${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
}
